import { useState } from 'react';
import newsletterService from '../services/newsletter.service';

/**
 * Hook personnalisé pour gérer l'inscription à la newsletter
 * @returns {Object} - État et fonctions pour gérer la newsletter
 */
export const useNewsletter = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState('');

  /**
   * Réinitialiser l'état
   */
  const resetState = () => {
    setError(null);
    setSuccess(false);
    setMessage('');
  };

  /**
   * S'inscrire à la newsletter
   * @param {string} email - L'adresse email
   */
  const subscribe = async (email) => {
    if (!email || !email.trim()) {
      setError('Veuillez saisir une adresse email');
      return;
    }

    setIsLoading(true);
    resetState();

    try {
      const result = await newsletterService.subscribe(email);
      
      if (result.success) {
        setSuccess(true);
        setMessage(result.message || 'Inscription réussie !');
      }
    } catch (err) {
      setError(err.message || 'Une erreur est survenue lors de l\'inscription');
      setSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Se désinscrire de la newsletter
   * @param {string} email - L'adresse email
   */
  const unsubscribe = async (email) => {
    if (!email || !email.trim()) {
      setError('Veuillez saisir une adresse email');
      return;
    }

    setIsLoading(true);
    resetState();

    try {
      const result = await newsletterService.unsubscribe(email);
      
      if (result.success) {
        setSuccess(true);
        setMessage(result.message || 'Désinscription réussie !');
      }
    } catch (err) {
      setError(err.message || 'Une erreur est survenue lors de la désinscription');
      setSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Vérifier si un email est déjà inscrit
   * @param {string} email - L'adresse email
   * @returns {Promise<boolean>} - True si l'email est déjà inscrit
   */
  const checkExisting = async (email) => {
    try {
      return await newsletterService.checkExistingEmail(email);
    } catch (err) {
      console.error('Erreur lors de la vérification:', err);
      return false;
    }
  };

  /**
   * Valider le format d'un email
   * @param {string} email - L'email à valider
   * @returns {boolean} - True si l'email est valide
   */
  const validateEmail = (email) => {
    return newsletterService.isValidEmail(email);
  };

  return {
    // État
    isLoading,
    error,
    success,
    message,
    
    // Actions
    subscribe,
    unsubscribe,
    checkExisting,
    validateEmail,
    resetState
  };
};

export default useNewsletter;
